import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    signal,
    OnInit,
    ViewEncapsulation,
    model,
    DestroyRef,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    IOnboardingReportData,
    IOnboardingReportWidget,
    IOnboardingReportContainer,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import { FiOnboardingReportGroupType } from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-enums';
import { FiOnboardingReportDateService } from '../services/fi-onboarding-report-date.service';
import { FiOnboardingReportService } from '../services/fi-onboarding-report.service';
import { IOnboardingReportParameter } from '../fi-onboarding-report';
import { FiOnboardingReportDataService } from '../services/fi-onboarding-report-data.service';
import { ObjectTypes } from '@fi-app/shared/types';
import { FiCommonSharedService } from '@fi-shared/fi-shared-services/fi-common-shared.service';
import { FiOnboardingReportHelperService } from '../services/fi-onboarding-report-helper.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ItemsListFilterDialogSnackbarService } from '@fi-app/shared/fi-items-list/fi-items-list-filter/services/items-list-filter-dialog-snackbar.service';

@Component({
    selector: 'fi-onboarding-report-dashboard',
    templateUrl: './fi-onboarding-report-dashboard.component.html',
    styleUrl: './fi-onboarding-report-dashboard.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-report-dashboard' },
    standalone: false,
})
export class FiOnboardingReportDashboardComponent implements OnInit {
    public reportContainer = model<IOnboardingReportContainer>();
    protected widgets = computed<IOnboardingReportWidget[]>(() => this.reportContainer()?.Widgets ?? []);

    private fiOnboardingReportDateService = inject(FiOnboardingReportDateService);
    private fiOnboardingReportDataService = inject(FiOnboardingReportDataService);
    private fiOnboardingReportService = inject(FiOnboardingReportService);
    private commonService = inject(FiCommonSharedService);
    private fiOnboardingReportHelperService = inject(FiOnboardingReportHelperService);
    private itemsListFilterDialogSnackbarService = inject(ItemsListFilterDialogSnackbarService);

    private destroyRef = inject(DestroyRef);

    protected widgetsReportDataMap = signal<Map<number, IOnboardingReportData>>(
        new Map<number, IOnboardingReportData>(),
    );
    private readonly reportDataEffect = effect(() => {
        const newMap = new Map<number, IOnboardingReportData>();
        this.reportContainer()?.Data?.forEach((data) => {
            newMap.set(data.WidgetId, data);
        });
        this.widgetsReportDataMap.set(newMap);
    });

    public ngOnInit(): void {
        this.subscribeOnDateRangeChanged();
        this.subscribeOnAddReportWidgetDialog();
        this.subscribeOnSaveReportWidget();
        this.subscribeOnReportContainerChange();
    }

    private subscribeOnDateRangeChanged(): void {
        this.fiOnboardingReportDateService.updateSummary$.subscribe((widget) => {
            this.fiOnboardingReportService.getReportData(this.getReportDataParam(widget)).then((data) => {
                this.widgetsReportDataMap.update((map) => new Map(map).set(widget.Id, data));
            });
        });
    }

    private subscribeOnAddReportWidgetDialog(): void {
        this.fiOnboardingReportHelperService.openAddReportWidgetDialog$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.fiOnboardingReportHelperService.openAddReportWidgetDialog();
            });
    }

    private subscribeOnSaveReportWidget(): void {
        this.fiOnboardingReportHelperService.saveReportWidget$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((widget) => {
                this.fiOnboardingReportService.saveReportWidget(widget).then((widgetId: number) => {
                    this.itemsListFilterDialogSnackbarService.showWidgetAddedSnackbar();
                    // todo: check if instead of realoding we can update current report container with new widget
                    this.reloadReportContainer();
                });
            });
    }

    private subscribeOnReportContainerChange(): void {
        this.fiOnboardingReportDataService.reportContainerUpdated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.loadReportContainer();
        });
    }

    private reloadReportContainer(): void {
        this.fiOnboardingReportDataService.resetReportContainer();
        this.fiOnboardingReportDataService.getReportContainer().then((reportContainer) => {
            this.reportContainer.set(reportContainer);
        });
    }

    protected onReportDrop(event: CdkDragDrop<IOnboardingReportWidget[]>): void {
        let widgets = this.widgets();
        moveItemInArray(widgets, event.previousIndex, event.currentIndex);
        this.commonService.updateSortIndexes(widgets, ObjectTypes.OnboardingReport);
        widgets = widgets.map((widget: IOnboardingReportWidget, index: number) => {
            widget.SortIndex = index;
            return widget;
        });
        this.reportContainer.update((container) => ({
            ...container,
            Widgets: widgets,
        }));
    }

    protected updateWidget(widget: IOnboardingReportWidget): void {
        this.fiOnboardingReportService.saveReportWidget(widget).then((id: number) => {
            this.fiOnboardingReportDataService.resetReportContainer();
            this.loadReportContainer();
        });
    }

    protected deleteWidget(widget: IOnboardingReportWidget) {
        this.fiOnboardingReportService.deleteReportWidget(widget.Id).then(() => {
            this.fiOnboardingReportDataService.resetReportContainer();
            this.loadReportContainer();
        });
    }

    // note: currently used only after rename/change description/delete widget
    // probably we don't need to update all report widget after changing one of them
    // instead we can update the map on the frontend (if custom widget are not shared among different users);
    private loadReportContainer(): void {
        this.fiOnboardingReportDataService.getReportContainer().then((reportContainer: IOnboardingReportContainer) => {
            this.reportContainer.set({...reportContainer});
        });
    }

    private getReportDataParam(widget: IOnboardingReportWidget): IOnboardingReportParameter {
        return {
            WidgetId: widget.Id,
            ReportType: widget.ReportType,
            Filter: widget.Settings.Filters[0],
            DateRange: this.fiOnboardingReportDateService.getDateRange(widget.Id),
            LoadParticipantsSummary: true,
        };
    }

    protected readonly FiOnboardingReportGroupType = FiOnboardingReportGroupType;
}
